import React, {useCallback, useState} from 'react';
import {PrimaryButton, SecondaryButton} from './index';
import {getInitials} from '../helpers';
import moment from 'moment/moment';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {
  addComment,
  deleteComment,
  fetchComments,
} from '../requests/queries/customer360';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import {getLighterShade} from '../routes/EasyOnboard/colorUtils';
import {useParams} from 'react-router-dom';

export const CommentSection = () => {
  const {slug} = useParams();
  const queryClient = useQueryClient();
  const {userDetails} = useSelector(state => state?.auth);
  const baseColor = userDetails?.company?.color_code || '#3f7cdb';

  const lighterShade = getLighterShade(baseColor, 93);

  const [comment, setComment] = useState('');
  const [next, setNext] = useState(3);
  const [comment_id, setCommentId] = useState('');

  const {data: comments, isLoading: commentsLoading} = useQuery(
    ['comments'],
    () => fetchComments(slug),
    {retry: false},
  );
  const {isLoading: addCommentLoading, mutate: addUserComment} = useMutation(
    ['add-comments', slug],
    data => addComment(slug, data),
    {
      onSuccess: () => {
        toast.success('Comment added successfully');
        queryClient.invalidateQueries('comments');
      },
    },
  );

  const {isLoading: deleteCommentLoading, mutate: deleteUserComment} =
    useMutation(
      'delete-comments',
      commentId => deleteComment(slug, commentId),
      {
        onSuccess: () => {
          toast.success('Comment deleted successfully');
          queryClient.invalidateQueries('comments');
        },
      },
    );

  const handleAddComment = useCallback(() => {
    if (!comment) {
      return toast.error('Please enter a comment');
    }
    if (comment.length) {
      addUserComment({comment});
      setComment('');
    }
  }, [comment, addUserComment]);
  const handleLoadMore = () => {
    setNext(next + 3);
  };
  return (
    <div className="rounded-md bg-white80 overflow-hidden grow xl:grow-0  border border-[#E4E3E6] w-full mt-5">
      <div className="p-4 ">
        <div className="flex items-center gap-1">
          <p className="text-xs font-medium uppercase text-grey">Comments</p>
          <div className="flex items-center justify-center w-4 h-4 pt-[2px] text-xs text-white rounded-full bg-brandBlue">
            {comments && comments?.comments?.length}
          </div>
        </div>

        <div
          style={{
            opacity: addCommentLoading || commentsLoading ? 0.4 : 1,
          }}
        >
          {(!addCommentLoading || !commentsLoading) &&
            comments &&
            comments?.comments?.slice(0, next)?.map((comment, index, data) => (
              <div
                key={comment?._id}
                className={`flex flex-col gap-1 mt-3 pb-2 ${
                  index === data.length - 1 ? '' : 'border-b border-b-[#e9e9e9]'
                }`}
                onMouseEnter={() => setCommentId(comment?._id)}
                onMouseLeave={() => setCommentId('')}
              >
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-brandBlue40 text-brandBlue flex items-center justify-center rounded-full text-xs font-medium">
                    {getInitials(
                      `${comment?.author.first_name} ${comment?.author.last_name}`,
                    ) || '-'}
                  </div>
                  <div className="w-full flex flex-col gap-1">
                    <div className="flex items-center justify-between w-full">
                      <p className="font-medium text-sm text-grey">
                        {`${comment?.author.first_name || '-'}
                          ${comment?.author.last_name || ''}`}
                      </p>
                      <p className="text-[8px] text-grey40 font-medium uppercase">
                        {moment(comment.createdAt).fromNow() || ''}
                      </p>
                    </div>
                    <p className="text-[12px] text-body">
                      {comment?.comment || '-'}
                    </p>
                  </div>
                </div>

                <div className="relative">
                  {comment_id === comment._id && (
                    <SecondaryButton
                      buttonText={
                        comment_id === comment._id && deleteCommentLoading
                          ? 'deleting...'
                          : 'delete'
                      }
                      fontSize="text-[9px]"
                      btnHeight="h-7"
                      xPadding="px-2"
                      yPadding="pb-[2px]"
                      className="absolute right-2 bottom-[0.1px] text-danger bg-danger"
                      onClick={() => {
                        setCommentId(comment?._id);
                        deleteUserComment(comment?._id);
                      }}
                      disabled={
                        comment_id === comment._id && deleteCommentLoading
                      }
                    />
                  )}
                </div>
              </div>
            ))}
          <div className="relative mt-4">
            <textarea
              onChange={e => setComment(e.target.value)}
              value={comment}
              name="comment"
              id="comment"
              data-smallertextarea={''}
            />
            <PrimaryButton
              buttonText={addCommentLoading ? 'sending...' : 'Send'}
              fontSize="text-xs"
              xPadding="px-2"
              yPadding="pt-[9px] pb-[7px]"
              className="absolute right-2 bottom-4"
              onClick={handleAddComment}
              disabled={!comment || addCommentLoading}
            />
          </div>
        </div>
      </div>
      {!commentsLoading && next < comments?.comments?.length && (
        <div
          className="flex justify-end w-full  px-4 py-2"
          style={{backgroundColor: lighterShade}}
        >
          <SecondaryButton
            className="flex items-start gap-2 "
            fontSize="text-xs"
            xPadding="px-2"
            yPadding="pt-[9px] pb-[7px]"
            btnHeight="h-7"
            buttonText="Load more"
            onClick={handleLoadMore}
          />
        </div>
      )}
    </div>
  );
};
